import React from 'react';
import styled from 'styled-components';
import {COLORS, LoadingIndicator, LOADING_MESSAGE} from '@fupa/fupa-uikit';
import {FlexContainer} from 'app/styles/content';
import MatchEventGoal from 'app/components/matchEvents/MatchEventGoal';
import MatchEventCard from 'app/components/matchEvents/MatchEventCard';
import MatchEventSubstitution from 'app/components/matchEvents/MatchEventSubstitution';
import MatchEventChance from 'app/components/matchEvents/MatchEventChance';
import MatchEventPenaltyFail from 'app/components/matchEvents/MatchEventPenaltyFail';
import MatchEventTimepenalty from 'app/components/matchEvents/MatchEventTimepenalty';
import MatchEventSingleText from 'app/components/matchEvents/MatchEventsSingleText';
import MatchEventPhoto from 'app/components/matchEvents/MatchEventPhoto';
import {TickerAuthorAssigned} from 'app/components/match/matchLive/tickerAuthorAssigned';
import {MatchEventLineup} from 'app/components/matchEvents/MatchEventLineup';
import TickerNewsUploaded from 'app/components/match/matchLive/TickerNewsUploaded';
import TickerGalleryUploaded from 'app/components/match/matchLive/TickerGalleryUploaded';
import {RMTFact} from 'app/components/match/matchLive/RMTFact';
import {array, bool, object} from 'prop-types';
import {StreamPlaceholder} from 'app/styles/stream';
import {Collapse, List, ListItem} from '@mui/material';
import {TransitionGroup} from 'react-transition-group';
import {AdSlot} from 'app/components/ads/AdSlot';
import SportVideoTeaser from 'app/components/match/matchLive/SportVideoTeaser';

const Container = styled(FlexContainer)`
  flex-direction: column;
`;

const displayMatchEvents = {
  goal: (matchEvent, match) => <MatchEventGoal key={matchEvent.id} event={matchEvent} match={match} />,
  card: matchEvent => <MatchEventCard key={matchEvent.id} event={matchEvent} />,
  substitute: matchEvent => <MatchEventSubstitution key={matchEvent.id} event={matchEvent} />,
  chance: matchEvent => <MatchEventChance key={matchEvent.id} event={matchEvent} />,
  penaltyfail: matchEvent => <MatchEventPenaltyFail key={matchEvent.id} event={matchEvent} />,
  text: matchEvent =>
    matchEvent && matchEvent.text ? (
      <MatchEventSingleText key={matchEvent.id} event={matchEvent} />
    ) : (
      <MatchEventPhoto key={matchEvent.id} event={matchEvent} />
    ),
  whistle: matchEvent => <MatchEventSingleText key={matchEvent.id} event={matchEvent} icon='whistle' />,
  timeout: matchEvent => <MatchEventSingleText key={matchEvent.id} event={matchEvent} />,
  timepenalty: matchEvent => <MatchEventTimepenalty key={matchEvent.id} event={matchEvent} />,
  teamfoul: matchEvent => (
    <MatchEventSingleText key={matchEvent.id} event={matchEvent} icon='teamFoul' iconColor={COLORS.neonRed} />
  ),
  lineupentered: matchEvent =>
    matchEvent.team ? <MatchEventLineup key={matchEvent.id} team={matchEvent.team} /> : null,
};

const checkStreamProtType = {
  matchevent: (entity, match) => (entity.type ? displayMatchEvents[entity.type]?.(entity, match) : null),
  'liveticker-eingetragen': entity => <TickerAuthorAssigned key={entity.id} author={entity} />,
  galerie: entity => <TickerGalleryUploaded key={entity.id} gallery={entity} />,
  news: entity => <TickerNewsUploaded key={entity.id} news={entity} />,
  fact: entity => (entity?.text ? <RMTFact key={entity.id} fact={entity.text} /> : null),
};

const Liveticker = ({events, isFetching, error, match}) => {
  const isVideoAllowed = match?.competition?.videoAllowed;

  const SportVideoElement = isVideoAllowed ? <SportVideoTeaser /> : null;

  const tickerUIElements = events
    .map(element => {
      if (element.type === 'adslot') {
        return <AdSlot key={element.key} adUnit={element.key} />;
      }
      return checkStreamProtType[element.type]?.(element.entity, match);
    })
    .filter(item => item);

  if (SportVideoElement) {
    tickerUIElements.push(SportVideoElement);
  }

  let fallback = null;
  if (error) {
    fallback = <StreamPlaceholder>Beim Laden der Meldungen ist ein Fehler aufgetreten.</StreamPlaceholder>;
  } else if (isFetching) {
    fallback = <LoadingIndicator message={LOADING_MESSAGE.loadingStream} bgColor />;
  } else if (tickerUIElements.length === 0 && !isFetching && !error) {
    fallback = <StreamPlaceholder>Keine Meldungen vorhanden.</StreamPlaceholder>;
  }

  return (
    <Container>
      <List>
        <TransitionGroup>
          {tickerUIElements.map(element => (
            <Collapse timeout={750} key={element.key}>
              <ListItem style={{all: 'inherit'}}>{element}</ListItem>
            </Collapse>
          ))}
        </TransitionGroup>
      </List>
      {fallback}
    </Container>
  );
};

Liveticker.propTypes = {
  events: array.isRequired,
  isFetching: bool.isRequired,
  error: object,
};

export {Liveticker};
